import { DefaultResponse, FileProps } from '@/common/types/common'
import { apiService } from './apiService'
import { companyEndpoints, fileEndpoints } from '@/api/apiEndpoint'

export interface CompanyData { 
  id: number;
  name: string;
  businessNumber: string;
  ceoName: string[];
  code: string;
  description: string;
  logo: string;
}

export interface MasterCompanyDto {
  id: number;
  code: string;
  name: string;
  businessNumber: string;
  currency: string;
  service: string[];
  logo?: string;
}
export interface CheckExistCompanyResponse {
  isExist: boolean;
  company?: {
    id: number;
    name: string;
    ceoName: string;
    description: string;
    businessNumber: string;
    logo: string;
    masterUser?: {
      id: number;
      name: string;
      email: string;
    }
  };
}
interface CheckCompanyStatusData {
  companyName: string
  ceoName: string
  businessNumber: string
  corporateNumber: string
  establishedDate: string
}

interface CheckCompanyStatusResponse {
  isExistCompany: boolean;
  isCertifiedCompanyName: boolean;
  /** 국세청(공공데이터) API 장애/응답 이상 — 입력값 불일치와 구분 */
  isApiError?: boolean;
}

export interface RegisterUserAndCompanyData {
  notificationId: string;
  companyEmail: string;
  companyEmailVerified: boolean;
  companyCode: string;
  isVerified: boolean;
  isPreStartup: boolean;
  isCeo: boolean;
  companyName: string;
  companyNameEn?: string;
  businessNumber?: string;
  corporateNumber?: string;
  isBusinessVerified?: boolean;
  residentNumber?: string;
  foundingDate?: string;
  ceoName: string;
  ceoEmail: string;
  name: string;
  phone: string;
  email: string;
  emailVerified: boolean;
  loginId?: string;
  password: string;
  userDi: string;
  userCi: string;
  token: string;
}

export interface RegisterCompanyData {
  notificationId: string;
  companyEmail: string;
  companyEmailVerified: boolean;
  companyCode: string;
  isVerified: boolean;
  isPreStartup: boolean;
  isCeo: boolean;
  companyName: string;
  companyNameEn?: string;
  businessNumber?: string;
  corporateNumber?: string;
  isBusinessVerified?: boolean;
  foundingDate?: string;
  ceoName: string;
  ceoEmail: string;
}

const NTS_API_ERROR_RESULT: CheckCompanyStatusResponse = {
  isExistCompany: false,
  isCertifiedCompanyName: false,
  isApiError: true,
}

const isNtsValidateSuccess = (response: any): boolean => {
  return response?.status_code === 'OK' && Array.isArray(response?.data) && response.data.length > 0
}

const requestNtsValidate = async (url: string, body: object): Promise<any> => {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  // 국세청/공공데이터 측 HTTP 오류 (400, 500 등)
  if (!res.ok) {
    throw new Error(`NTS_API_HTTP_${res.status}`)
  }

  const json = await res.json()

  // 인증키 오류 등: { code: -4, msg: '...' } 형태로 data 없이 오는 경우
  if (!isNtsValidateSuccess(json)) {
    throw new Error('NTS_API_INVALID_RESPONSE')
  }

  return json
}

export const companyService = {
  async checkCompanyStatus(data: CheckCompanyStatusData): Promise<CheckCompanyStatusResponse> {
    const key = "9bBWSMsQwxGfVp%2Fe2oQCWfK0q%2FU0sYYP0rSUVzPWT1Vd0SYxBgxYNWKcu8PRSzHJHNvvEOSwE%2FKlZW8Uco59uA%3D%3D"
    const url = `https://api.odcloud.kr/api/nts-businessman/v1/validate?serviceKey=${key}&returnType=JSON`
    
    const firstBody = {
      businesses: [
        {
          b_no: data.businessNumber,
          start_dt: data.establishedDate,
          p_nm: data.ceoName,
          corp_no: data.corporateNumber,
        }
      ]
    }

    const secondBody = {
      businesses: [
        {
          b_nm: data.companyName,
          b_no: data.businessNumber,
          start_dt: data.establishedDate,
          p_nm: data.ceoName,
          corp_no: data.corporateNumber,
        }
      ]
    }

    try {
      const firstResponse = await requestNtsValidate(url, firstBody)

      // valid !== '01' : 입력 정보 불일치 (국세청 API는 정상 응답)
      if (firstResponse.data[0].valid !== '01') {
        return {
          isExistCompany: false,
          isCertifiedCompanyName: false,
          isApiError: false,
        }
      }
      
      const secondResponse = await requestNtsValidate(url, secondBody)

      return {
        isExistCompany: true,
        isCertifiedCompanyName: secondResponse.data[0].valid === '01',
        isApiError: false,
      }
      
    } catch (err: any) {
      console.error(err)
      return NTS_API_ERROR_RESULT
    }
  },

  async checkExistCompany(businessNumber: string): Promise<CheckExistCompanyResponse> {
    try {
      const response = await apiService.post<CheckExistCompanyResponse>(companyEndpoints.checkExistCompany(), {businessNumber});
      return response;
    } catch (error) {
      return {
        isExist: false,
      }
    }

  },

  async registerUserAndCompany(data: any): Promise<DefaultResponse> {
    try {
      const response = await apiService.post<DefaultResponse>(companyEndpoints.registerUserAndCompany(), data);
      return response;
    } catch (error: any) {
      // 서버에서 반환한 에러 메시지가 있으면 사용, 없으면 기본 메시지
      const errorMessage = error?.response?.data?.message || error?.message || '회원 가입 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  async registerCompany(data: any): Promise<DefaultResponse> {
    try {
      const response = await apiService.post<DefaultResponse>(companyEndpoints.registerCompany(), data);
      return response;
    } catch (error: any) {
      // 서버에서 반환한 에러 메시지가 있으면 사용, 없으면 기본 메시지
      const errorMessage = error?.response?.data?.message || error?.message || '회사 등록 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },


  async applyCompanyJoinRequest(data: {
    companyId: number; email: string;
  }): Promise<DefaultResponse> {
    try {
      const response = await apiService.post<DefaultResponse>(companyEndpoints.applyCompany(), data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || '회사 가입 신청 중 오류가 발생했습니다.'
      };
    }
  },

  async matchingCompany(data: {
    serviceType: string; companyId: number; email: string; businessPlanFileId: number; companyCode: string;
  }): Promise<DefaultResponse> {
    try {
      const response = await apiService.post<DefaultResponse>(companyEndpoints.matchCompany(), data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error?.message || '회사 매칭 중 오류가 발생했습니다.'
      };
    }
  },

  async getSearchCompany(keyword: string): Promise<CompanyData[]> {
    try { 
      const response = await apiService.get<CompanyData[]>(companyEndpoints.searchCompany(keyword));
      return response;
    } catch (error) {
      return [];
    }
  },

  async getMasterCompanies(): Promise<MasterCompanyDto[]> {
    try {
      const response = await apiService.get<MasterCompanyDto[]>(companyEndpoints.getMasterCompanies());
      return response;
    } catch (error) {
      return [];
    }
  },
}
